/**
 * /dashboard/people/:id (§2.6). Official profile (screenshots/19) with the tabs Overview · Votes & legislation (29)
 * · Money (30) · Timeline · Appearances, same tabs as the public ward page; resident profile (20, offices and the
 * platform team only, or the resident's own verified profile with "This is me", card 6).
 * Paints from snapshot:person:{id}; tab changes cross-fade with a 6px rise (motion #1).
 */
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ScrollText, Tag, Ellipsis, Eye, MessageSquarePlus, Plus, Share2, ShieldCheck, Sparkles, Trash2, Check } from "lucide-react";
import { peopleStore, usePeople } from "./peopleStore";
import { followsStore, findFollow, useFollows } from "../stores/followsStore";
import { toastStore } from "../stores/toastStore";
import { recipes, prefersReducedMotion, reducedFallback } from "../motion";
import { clock } from "../lib/format";
import { CountBadge } from "../components/shared/ui";
import { PersonImg, StanceChip, Watch, mdy } from "./atoms";
import { LeaningCard, HowEstimated } from "./LeaningCard";
import { ProfileOverview } from "./ProfileOverview";
import { Appearances } from "./Appearances";
import { VotesTab } from "./VotesTab";
import { MoneyTab } from "./MoneyTab";
import { TimelineTab } from "./TimelineTab";
import { TabErrorBoundary } from "../components/shared/States";
import { onTablistKey } from "../hooks/useTabs";
import { peopleApi } from "./peopleStore";
import type { PersonProfile } from "./model";
import { navigate } from "../app/router";
import { useBusy } from "../hooks/useBusy";
import { useDismiss } from "../hooks/useDismiss";
import { Crumbs, sectionCrumb } from "../app/crumbs";

export type ProfileTab = "overview" | "votes" | "money" | "timeline" | "appearances";

export function ProfilePage({ id, tab, onTab, canFlag, platformAdmin }: {
  id: string; tab: ProfileTab; onTab: (t: ProfileTab) => void; canFlag: boolean; platformAdmin: boolean;
}) {
  const p = usePeople((s) => s.profiles[id]);
  // Not inlined (e.g. the snapshot failed): fetch it once.
  useEffect(() => { if (!p) peopleStore.getState().loadProfile(id); }, [id, p]);
  if (!p) return null;
  if ("error" in p) return <p className="empty-panel">This person isn't on the dashboard.</p>;
  if (p.type === "resident" || p.type === "org_rep" || p.type === "other") return <PrivateProfile p={p} canFlag={canFlag} platformAdmin={platformAdmin} />;
  const official = !!p.official;
  const tabs: [ProfileTab, string, string?][] = [
    ["overview", "Overview"],
    ...(official ? [["votes", "Votes & legislation", `${p.official!.votes.divided.length} divided`] as [ProfileTab, string, string], ["money", "Money"] as [ProfileTab, string], ["timeline", "Timeline"] as [ProfileTab, string]] : []),
    ["appearances", "Appearances", String(p.appearances)],
  ];
  const current = tabs.some(([t]) => t === tab) ? tab : "overview";
  return (
    <div className="profile-page">
      <Crumbs trail={[sectionCrumb("people"), { label: p.name }]} />
      <Header p={p} platformAdmin={platformAdmin} />
      <div className="p-ledger">
        <Stat v={p.stats.meetings} l="Meetings spoken at" />
        <Stat v={p.stats.moments} l="Moments on record" />
        <Stat v={p.stats.positions} l="Positions taken" />
        {p.stats.attendance && <Stat v={`${fmtPct(p.stats.attendance.pct)}%`} l={`Attendance · ${p.stats.attendance.rollCalls.toLocaleString("en-US")} roll calls`} />}
        {p.stats.lastSpoke && <Stat v={mdy(p.stats.lastSpoke.date)} l={`Last spoke · ${p.stats.lastSpoke.body}`} />}
      </div>
      <div className="tabs p-tabs" role="tablist" aria-label="Profile" onKeyDown={onTablistKey}>
        {tabs.map(([t, label, count]) => (
          <button key={t} role="tab" className="tab" aria-selected={current === t} onClick={() => onTab(t)}
>
            {label}{count && <CountBadge>{count}</CountBadge>}
          </button>
        ))}
      </div>
      <TabPanel key={current}>
        <TabErrorBoundary tabLabel={tabs.find(([t]) => t === current)![1]}>
        {current === "overview" && <ProfileOverview p={p} canFlag={canFlag} onAppearances={() => onTab("appearances")} />}
        {current === "votes" && p.official && <VotesTab rec={p.official} />}
        {current === "money" && p.official && <MoneyTab rec={p.official} />}
        {current === "timeline" && p.official && <TimelineTab ward={p.official.ward} name={p.name} />}
        {current === "appearances" && <Appearances p={p} />}
        </TabErrorBoundary>
      </TabPanel>
    </div>
  );
}

const fmtPct = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

/** Tab change: cross-fade + 6px rise, 200 ms (motion #1). */
function TabPanel({ children }: { children: React.ReactNode }) {
  const el = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const r = recipes.tabChange;
    el.current?.animate(prefersReducedMotion() ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 0, transform: `translateY(${r.from.y}px)` }, { opacity: 1, transform: "none" }],
      { duration: prefersReducedMotion() ? reducedFallback.duration : r.duration, easing: r.easing });
  }, []);
  return <div ref={el} role="tabpanel">{children}</div>;
}

const Stat = ({ v, l }: { v: React.ReactNode; l: string }) => <div className="p-ledger__stat"><b>{v}</b><span>{l}</span></div>;

function Header({ p, platformAdmin }: { p: PersonProfile; platformAdmin: boolean }) {
  const follow = useFollows((s) => findFollow(s.follows, "person", p.id));
  const [menu, setMenu] = useState(false);
  const moreRef = useRef<HTMLSpanElement>(null);
  useDismiss(moreRef, menu, () => setMenu(false));
  return (
    <header className="p-head">
      <PersonImg image={p.image} name={p.name} size={76} w={136} h={76} label />
      <div className="p-head__id">
        <div className="p-head__name"><h1>{p.name}</h1></div>
        <div className="p-head__meta">
          <span>{p.role}</span>
          {p.image.kind === "portrait" && <><span className="p-sep">·</span><small>Official portrait</small></>}
        </div>
      </div>
      <div className="p-head__actions">
        <button className="btn" onClick={() => dispatchEvent(new CustomEvent("dashboard:ask", { detail: { personId: p.id, name: p.name } }))}><Sparkles size={14} aria-hidden />Ask about this person</button>
        <ShareButton />
        {follow
          ? <button className="btn" onClick={() => followsStore.getState().unfollow(follow.id)}><Check size={14} aria-hidden />Following</button>
          : <button className="btn btn--primary" onClick={() => followsStore.getState().follow({ type: "person", refId: p.id, label: p.name })}><Plus size={14} aria-hidden />Follow</button>}
        {platformAdmin && (
          <span className="p-more" ref={moreRef}>
            <button className="p-more__btn" aria-label="More" aria-expanded={menu} onClick={() => setMenu(!menu)}><Ellipsis size={16} /></button>
            {menu && <div className="p-pop p-more__menu" role="menu"><DeleteButton p={p} label="Delete person" /></div>}
          </span>
        )}
      </div>
    </header>
  );
}

function ShareButton() {
  return (
    <button className="btn" onClick={() => { navigator.clipboard?.writeText(location.href); toastStore.getState().show({ message: "Link copied" }); }}>
      <Share2 size={14} aria-hidden />Share
    </button>
  );
}

function DeleteButton({ p, label, className }: { p: PersonProfile; label: string; className?: string }) {
  return (
    <button className={className ?? "p-danger-item"} role={className ? undefined : "menuitem"} onClick={async () => {
      if (!confirm(`${label}: ${p.name}?`)) return;
      const r = await fetch(`/api/dashboard/people/${encodeURIComponent(p.id)}`, { method: "DELETE", credentials: "include" });
      if (r.ok) navigate("/dashboard/people");
    }}><Trash2 size={14} aria-hidden />{label}</button>
  );
}

/** Resident / organization rep profile (screenshots/20): video frame only, the name as given at the podium. */
function PrivateProfile({ p, canFlag, platformAdmin }: { p: PersonProfile; canFlag: boolean; platformAdmin: boolean }) {
  const testimony = p.positions;
  return (
    <div className="profile-page profile-page--private">
      <a className="back-link" href="/dashboard/people"><ArrowLeft size={14} aria-hidden />People</a>
      <header className="p-head p-head--private">
        <PersonImg image={p.image} name={p.name} w={136} h={76} label />
        <div className="p-head__id">
          <div className="p-head__name"><h1>{p.name}</h1></div>
          <div className="p-head__meta"><span>{p.role}</span></div>
        </div>
        <div className="p-head__actions">
          <ShareButton />
          {platformAdmin && <DeleteButton p={p} label="Remove from dashboard" className="btn p-btn-danger" />}
        </div>
      </header>
      <p className="p-privacy"><ShieldCheck size={15} aria-hidden />Residents' profiles show only what they said on the public record. Estimates are labelled, show their confidence and quotes, and can be flagged. Residents can request removal.</p>
      <div className="p-cols">
        <div className="p-main">
          <div className="p-sec-head"><h2>Public comments</h2><CountBadge>{p.appearancesList.length}</CountBadge></div>
          {p.appearancesList.map((a) => (
            <div key={a.id} className="p-comment">
              <PersonImg image={a.frame} name={p.name} w={160} h={90} label />
              <div className="p-comment__body">
                <div className="p-app__meeting"><i className="p-dot" data-kind={/^City Council/.test(a.body) ? "council" : "committee"} aria-hidden /><b>{a.meetingLabel}</b><small>{mdy(a.date, { weekday: "short", month: "short", day: "numeric" })}</small></div>
                <div className="p-comment__sum"><span className="ai-chip"><Sparkles size={11} aria-hidden />AI summary</span><p>{a.summary}</p></div>
                <Watch meetingId={a.meetingId} at={a.from}>Watch the full comment at {clock(a.from)}</Watch>
              </div>
            </div>
          ))}
        </div>
        <aside className="p-side">
          {p.claimed && <ThisIsMe p={p} />}
          {testimony.length > 0 && (
            <section className="p-card">
              <h3>Positions from their testimony</h3>
              {testimony.map((x) => (
                <div key={x.id} className="p-tpos">
                  <i className="type-badge" data-type={x.recordId ? "legislation" : "topic"} aria-hidden>{x.recordId ? <ScrollText size={11} /> : <Tag size={11} />}</i>
                  <span><b>{x.label}</b><small>{x.area} · {x.recordId ?? "topic"}</small></span>
                  <StanceChip stance={x.stance} />
                </div>
              ))}
            </section>
          )}
          {p.leaning && <LeaningCard personId={p.id} estimate={p.leaning} canFlag={canFlag} />}
        </aside>
      </div>
    </div>
  );
}

/** "This is me" (card 6): the resident's own verified profile. */
function ThisIsMe({ p }: { p: PersonProfile }) {
  const [how, setHow] = useState(false);
  const [context, setContext] = useState(false);
  const [text, setText] = useState("");
  const { busy, run } = useBusy();
  return (
    <section className="p-card p-me">
      <div className="p-me__who"><PersonImg image={p.image} name={p.name} w={56} h={32} /><span><b>{p.name}</b><small>Verified · it's you</small></span></div>
      <button className="p-me__row" onClick={() => setContext(!context)}><MessageSquarePlus size={14} aria-hidden />Add context to a comment</button>
      {context && p.appearancesList[0] && (
        <form className="p-me__ctx" onSubmit={(e) => {
          e.preventDefault();
          // On failure the form stays open with the text, so nothing typed is lost.
          void run(() => peopleApi(`${encodeURIComponent(p.id)}/context`, { method: "POST", body: JSON.stringify({ appearanceId: p.appearancesList[0].id, text }) })
            .then(() => { setText(""); setContext(false); }, () => {}));
        }}>
          <textarea aria-label="Context" value={text} onChange={(e) => setText(e.target.value)} />
          <button className="btn btn--primary" type="submit" disabled={busy} aria-busy={busy || undefined}><Check size={13} aria-hidden /></button>
        </form>
      )}
      <button className="p-me__row" onClick={() => setHow(true)}><Eye size={14} aria-hidden />See how your estimate was made</button>
      <button className="p-me__row p-me__row--danger" onClick={async () => { if (confirm("Request removal?")) await peopleApi(`${encodeURIComponent(p.id)}/removal`, { method: "POST" }); }}><Trash2 size={14} aria-hidden />Request removal</button>
      {how && <HowEstimated onClose={() => setHow(false)} />}
    </section>
  );
}
